import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Linkedin, Send, Loader2 } from "lucide-react";

import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { IMAGES } from "@/data/content";
import { submitContact } from "@/lib/api";
import { useSettingValue } from "@/context/SiteConfig";

const FIELD =
  "w-full rounded-xl border border-hairline bg-white px-4 py-3 text-inktext placeholder:text-steel/60 focus:outline-none focus:border-accent2 focus:ring-2 focus:ring-accent2/20 transition-colors";
const LABEL = "block text-sm font-semibold text-navy mb-2";

const SUBJECTS = [
  "General Inquiry",
  "Procurement Support",
  "Vendor Partnership",
  "Capability Statement",
  "Other",
];

export default function Contact() {
  useSeo({
    title: "Contact",
    description:
      "Contact Meridian Vanguard Solutions to discuss government procurement support or supplier partnership opportunities.",
    path: "/contact",
    breadcrumb: [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }],
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "General Inquiry",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const email = useSettingValue("email", "info@meridianvanguardsolutions.com");
  const phone = useSettingValue("phone", "Coming Soon");
  const office = useSettingValue("office_address", "175 Capital Blvd, Suite 402, Rocky Hill, CT 06067");
  const hours = useSettingValue("business_hours", "Monday–Friday, 8:00 AM–5:00 PM");
  const linkedin = useSettingValue("linkedin_url", "https://www.linkedin.com");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.message.trim()) e.message = "Message is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", company: "", subject: "General Inquiry", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Get In Touch"
        titleLines={["Contact Meridian", "Vanguard Solutions"]}
        subtitle="Reach out to discuss procurement support or a supplier partnership. We respond promptly and professionally."
        image={IMAGES.heroCapitol}
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />

      <section className="bg-white py-24 md:py-32">
        <div className="w-full max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <h2 className="font-heading text-3xl font-bold text-navy tracking-tight">Send us a message</h2>
            <p className="mt-3 text-steel">Complete the form and a member of our team will follow up.</p>

            <form data-testid="contact-form" onSubmit={onSubmit} className="mt-9 space-y-6" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="c-name" className={LABEL}>Name *</label>
                  <input id="c-name" data-testid="contact-name" className={FIELD} value={form.name} onChange={update("name")} placeholder="Full name" />
                  {errors.name && <p className="mt-1.5 text-sm text-red-700">{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="c-email" className={LABEL}>Email *</label>
                  <input id="c-email" type="email" data-testid="contact-email" className={FIELD} value={form.email} onChange={update("email")} placeholder="name@agency.gov" />
                  {errors.email && <p className="mt-1.5 text-sm text-red-700">{errors.email}</p>}
                </div>
                <div>
                  <label htmlFor="c-phone" className={LABEL}>Phone</label>
                  <input id="c-phone" data-testid="contact-phone" className={FIELD} value={form.phone} onChange={update("phone")} placeholder="(000) 000-0000" />
                </div>
                <div>
                  <label htmlFor="c-company" className={LABEL}>Company / Agency</label>
                  <input id="c-company" data-testid="contact-company" className={FIELD} value={form.company} onChange={update("company")} placeholder="Organization name" />
                </div>
              </div>

              <div>
                <label className={LABEL}>Subject</label>
                <Select value={form.subject} onValueChange={(v) => setForm((f) => ({ ...f, subject: v }))}>
                  <SelectTrigger data-testid="contact-subject" className={`${FIELD} h-auto`}>
                    <SelectValue placeholder="Select a subject" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {SUBJECTS.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`subject-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label htmlFor="c-message" className={LABEL}>Message *</label>
                <textarea id="c-message" rows={5} data-testid="contact-message" className={FIELD} value={form.message} onChange={update("message")} placeholder="How can we help?" />
                {errors.message && <p className="mt-1.5 text-sm text-red-700">{errors.message}</p>}
              </div>

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 rounded-xl bg-navy text-white px-8 py-4 font-semibold hover:bg-accent2 transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          </Reveal>

          {/* Business info */}
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="rounded-xl bg-navy text-white p-9">
              <h3 className="font-heading text-2xl font-semibold">Business Information</h3>
              <ul className="mt-8 space-y-6">
                <li className="flex gap-4">
                  <span className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Mail size={20} className="text-accent2" /></span>
                  <div>
                    <p className="text-white/60 text-sm">Business Email</p>
                    <a href={`mailto:${email}`} className="font-medium hover:text-accent2 transition-colors break-words">{email}</a>
                  </div>
                </li>
                <li className="flex gap-4">
                  <span className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Phone size={20} className="text-accent2" /></span>
                  <div><p className="text-white/60 text-sm">Phone</p><p className="font-medium">{phone}</p></div>
                </li>
                <li className="flex gap-4">
                  <span className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><MapPin size={20} className="text-accent2" /></span>
                  <div><p className="text-white/60 text-sm">Office</p><p className="font-medium">{office}</p></div>
                </li>
                <li className="flex gap-4">
                  <span className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0"><Clock size={20} className="text-accent2" /></span>
                  <div><p className="text-white/60 text-sm">Business Hours</p><p className="font-medium">{hours}</p></div>
                </li>
              </ul>
              <a
                href={linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-linkedin"
                className="mt-8 inline-flex items-center gap-2 rounded-xl border border-white/25 px-5 py-3 text-sm font-semibold hover:bg-white hover:text-navy transition-colors"
              >
                <Linkedin size={18} /> Connect on LinkedIn
              </a>
            </div>

            {/* Office location */}
            <div data-testid="contact-office-location" className="mt-6 rounded-xl border border-hairline overflow-hidden">
              <img
                src="/office.png"
                alt="Meridian Vanguard Solutions office at 175 Capital Blvd, Suite 402, Rocky Hill, CT"
                loading="lazy"
                className="w-full h-56 object-cover"
              />
              <div className="flex items-start gap-3 bg-lightgray px-6 py-5">
                <MapPin size={20} className="text-accent2 mt-0.5 shrink-0" />
                <div>
                  <p className="font-heading font-semibold text-navy">Our Office</p>
                  <p className="mt-1 text-sm text-steel">175 Capital Blvd, Suite 402<br />Rocky Hill, CT 06067</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
